import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, Camera, Send, DollarSign, Plus, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("dd_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const STATUSES = ["new_lead", "scheduled", "in_progress", "completed"];
const STATUS_LABELS = { new_lead: "New Lead", scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed" };
const STATUS_COLORS = { new_lead: "#3B82F6", scheduled: "#F59E0B", in_progress: "#D95D39", completed: "#16A34A" };

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [note, setNote] = useState("");
  const [invoiceForm, setInvoiceForm] = useState({ amount: "", description: "", items: "" });
  const [invoices, setInvoices] = useState([]);
  const [showInvoice, setShowInvoice] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  const fetchJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobs/${id}`, auth());
      setJob(res.data);
      if (res.data.photo_path) {
        try {
          const photoRes = await axios.get(`${API}/files/${res.data.photo_path}`, { responseType: "blob" });
          setPhotoUrl(URL.createObjectURL(photoRes.data));
        } catch { /* no photo */ }
      }
    } catch { navigate("/dashboard"); }
  }, [id, navigate]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices?job_id=${id}`, auth());
      setInvoices(res.data);
    } catch { /* ok */ }
  }, [id]);

  useEffect(() => { fetchJob(); fetchInvoices(); }, [fetchJob, fetchInvoices]);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/jobs/${id}/status`, { status }, auth());
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      fetchJob();
    } catch { toast.error("Failed to update status"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${API}/jobs/${id}/notes`, { text: note }, auth());
      setNote("");
      toast.success("Note added");
      fetchJob();
    } catch { toast.error("Failed to add note"); }
  };

  const createInvoice = async () => {
    const amt = parseFloat(invoiceForm.amount);
    if (!amt || amt <= 0 || !invoiceForm.description) { toast.error("Enter amount and description"); return; }
    try {
      const items = invoiceForm.items ? invoiceForm.items.split("\n").filter(Boolean).map(i => ({ description: i, amount: 0 })) : [];
      await axios.post(`${API}/invoices`, { job_id: id, amount: amt, description: invoiceForm.description, line_items: items }, auth());
      toast.success("Invoice created");
      setInvoiceForm({ amount: "", description: "", items: "" });
      setShowInvoice(false);
      fetchInvoices();
    } catch { toast.error("Failed to create invoice"); }
  };

  const sendPaymentLink = async (invId) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/invoices/${invId}/checkout`, { origin_url: origin }, auth());
      if (res.data.url) {
        await navigator.clipboard.writeText(res.data.url);
        toast.success("Payment link copied to clipboard! Share with customer.");
      }
    } catch { toast.error("Failed to generate payment link"); }
  };

  if (!job) return <div className="p-8 text-[#64748B]">Loading...</div>;

  return (
    <div className="p-6 sm:p-8 max-w-4xl" data-testid="job-detail-page">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#9CA3AF] text-sm hover:text-[#D95D39] mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter">{job.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-[#9CA3AF] text-sm">
            <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{job.phone}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.address}</span>
          </div>
        </div>
        <span className="text-sm font-bold px-3 py-1.5 rounded-sm" style={{ backgroundColor: STATUS_COLORS[job.status] + "15", color: STATUS_COLORS[job.status] }} data-testid="job-status-badge">
          {STATUS_LABELS[job.status]}
        </span>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="border border-[#1F2937] rounded-sm p-4 bg-[#0F1A15]">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Animal Type</p>
          <p className="text-[#F8FAFC] font-semibold">{job.animal_type}</p>
        </div>
        <div className="border border-[#1F2937] rounded-sm p-4 bg-[#0F1A15]">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Urgency</p>
          <p className={`font-semibold ${job.urgency === "emergency" ? "text-[#DC2626]" : job.urgency === "urgent" ? "text-[#F59E0B]" : "text-[#9CA3AF]"}`}>{job.urgency}</p>
        </div>
      </div>
      {job.message && (
        <div className="border border-[#1F2937] rounded-sm p-4 bg-[#0F1A15] mb-6">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Customer Message</p>
          <p className="text-[#9CA3AF] text-sm">{job.message}</p>
        </div>
      )}
      {photoUrl && (
        <div className="border border-[#1F2937] rounded-sm p-4 bg-[#0F1A15] mb-6" data-testid="job-photo">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Customer Photo</p>
          <img src={photoUrl} alt="Customer submitted" className="max-w-full max-h-64 rounded-sm object-contain" />
        </div>
      )}

      {/* Status Pipeline */}
      <div className="border border-[#1F2937] rounded-sm p-5 bg-[#0F1A15] mb-6" data-testid="status-pipeline">
        <p className="text-[#64748B] text-xs uppercase tracking-wider mb-3">Update Status</p>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => updateStatus(s)} disabled={s === job.status} data-testid={`status-btn-${s}`} className={`px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors duration-150 ${s === job.status ? "opacity-50 cursor-default" : "hover:opacity-80 cursor-pointer"}`} style={{ backgroundColor: STATUS_COLORS[s] + "20", color: STATUS_COLORS[s], border: `1px solid ${STATUS_COLORS[s]}30` }}>
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="border border-[#1F2937] rounded-sm p-5 bg-[#0F1A15] mb-6" data-testid="notes-section">
        <p className="text-[#64748B] text-xs uppercase tracking-wider mb-3">Internal Notes</p>
        <div className="space-y-2 mb-4">
          {(!job.notes || job.notes.length === 0) && <p className="text-[#64748B] text-sm italic">No notes yet</p>}
          {(job.notes || []).map((n, i) => (
            <div key={i} className="border border-[#1F2937] rounded-sm p-3 bg-[#0B110E]">
              <p className="text-[#F8FAFC] text-sm">{n.text}</p>
              <p className="text-[#64748B] text-xs mt-1">{new Date(n.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." rows={2} className="bg-[#0B110E] border-[#1F2937] text-[#F8FAFC] rounded-sm text-sm flex-1 focus-visible:ring-[#D95D39]" />
          <button onClick={addNote} data-testid="add-note-btn" className="bg-[#D95D39] hover:bg-[#c04d2d] text-white px-4 rounded-sm transition-colors duration-150 self-end"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Invoices */}
      <div className="border border-[#1F2937] rounded-sm p-5 bg-[#0F1A15]" data-testid="invoices-section">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#64748B] text-xs uppercase tracking-wider">Invoices</p>
          <button onClick={() => setShowInvoice(!showInvoice)} data-testid="create-invoice-toggle" className="text-[#D95D39] text-xs font-bold uppercase tracking-wider flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {showInvoice ? "Cancel" : "Create Invoice"}</button>
        </div>
        {showInvoice && (
          <div className="border border-[#1F2937] rounded-sm p-4 bg-[#0B110E] mb-4 space-y-3" data-testid="invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#9CA3AF] text-xs mb-1 block">Amount ($)</Label>
                <Input data-testid="invoice-amount" type="number" step="0.01" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] h-9 rounded-sm text-sm focus-visible:ring-[#D95D39]" />
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs mb-1 block">Description</Label>
                <Input data-testid="invoice-description" value={invoiceForm.description} onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })} placeholder="e.g. Raccoon removal" className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] h-9 rounded-sm text-sm focus-visible:ring-[#D95D39]" />
              </div>
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-1 block">Line Items (one per line, optional)</Label>
              <Textarea data-testid="invoice-items" value={invoiceForm.items} onChange={(e) => setInvoiceForm({ ...invoiceForm, items: e.target.value })} rows={2} placeholder="Trapping service&#10;Seal-off repair" className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] rounded-sm text-sm focus-visible:ring-[#D95D39]" />
            </div>
            <button onClick={createInvoice} data-testid="submit-invoice-btn" className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-bold uppercase tracking-wider text-xs px-6 py-2.5 rounded-sm transition-colors duration-150">Create Invoice</button>
          </div>
        )}
        {invoices.length === 0 && !showInvoice && <p className="text-[#64748B] text-sm italic">No invoices created</p>}
        {invoices.map((inv) => (
          <div key={inv.id} className="border border-[#1F2937] rounded-sm p-4 bg-[#0B110E] mb-2" data-testid={`invoice-${inv.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#F8FAFC] font-semibold text-sm">${parseFloat(inv.amount).toFixed(2)} — {inv.description}</p>
                <p className="text-[#64748B] text-xs mt-0.5">
                  Status: <span className={inv.status === "paid" ? "text-[#16A34A]" : "text-[#F59E0B]"}>{inv.status}</span>
                  {" | "}{new Date(inv.created_at).toLocaleDateString()}
                </p>
              </div>
              {inv.status !== "paid" && (
                <button onClick={() => sendPaymentLink(inv.id)} data-testid={`send-payment-${inv.id}`} className="bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#16A34A] text-xs font-bold px-3 py-1.5 rounded-sm hover:bg-[#16A34A]/20 flex items-center gap-1 transition-colors duration-150">
                  <Send className="w-3 h-3" /> Payment Link
                </button>
              )}
              {inv.status === "paid" && <CheckCircle className="w-5 h-5 text-[#16A34A]" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
